import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native';

// Session storage helpers
const getSessionFlag = (key) => {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key) === 'true';
  }
  return false;
};

const setSessionFlag = (key, value) => {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(key, value ? 'true' : 'false');
  }
};
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { useDailyDecision } from '../context/DailyDecisionContext';
import { useCharacterPopup } from '../context/CharacterPopupContext';
import DashboardSidebar from '../components/DashboardSidebar';
import AnimatedCard from '../components/AnimatedCard';
import RightSidebar from '../components/RightSidebar';
import { progressService } from '../services/progressService';
import { courseService } from '../services/courseService';
import { rskService } from '../services/rskService';
import V12DiagnosticPopup from '../components/V12DiagnosticPopup';
import { v12DiagnosticService } from '../services/v12DiagnosticService';
import PersistentWidgetIndicator from '../components/PersistentWidgetIndicator';
import { usePersistentWidget } from '../context/PersistentWidgetContext';

const MyDashboardScreen = () => {
  const { palette } = useTheme();
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const { forceShowDailyDecision, checkDailyDecisionStatus, openReviewMode: openDailyDecisionReview, openDailyDecision, hasCompletedToday: dailyDecisionCompleted } = useDailyDecision();
  const { openReviewMode: openPersistentWidgetReview, availableWidgets, widgetStatuses, openPersistentWidget } = usePersistentWidget();
  const { showWelcome, showTip, showEncouragement } = useCharacterPopup();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGlossary, setShowGlossary] = useState(true);
  
  // V12 Diagnostic state
  const [showV12Popup, setShowV12Popup] = useState(false);
  const [v12DueInfo, setV12DueInfo] = useState(null);
  const [v12Status, setV12Status] = useState(null);
  const [enrollmentDate, setEnrollmentDate] = useState(null);

  useEffect(() => {
    if (user) {
      loadUserProgress();
      // Load glossary preference
      const glossaryPref = user.preferences?.showGlossary ?? true;
      setShowGlossary(glossaryPref);
      
      // Check V12 diagnostic status
      checkV12Status();
      
      // Show welcome popup once per session (first popup to show)
      const welcomeShown = getSessionFlag('welcomePopupShown');
      if (!welcomeShown) {
        setTimeout(() => {
          const firstName = user.displayName?.split(' ')[0] || 'there';
          showWelcome(
            `Welcome back, ${firstName}!`,
            "Ready to make progress today? Let's crush it! 💪",
            { duration: 6000 }
          );
          setSessionFlag('welcomePopupShown', true);
        }, 2000); // 2s delay - shows first
      }
      
      // Check daily decision status after welcome popup has time to show
      setTimeout(() => {
        checkDailyDecisionStatus();
      }, 3000); // 3s delay before checking (daily decision popup will wait additional 8s)
    }
  }, [user]);

  const checkV12Status = async () => {
    if (!user?.uid) return;
    
    try {
      // Get the user's earliest enrollment date
      const { courses } = await progressService.getAllUserProgress(user.uid);
      let earliestEnrollment = null;
      
      if (courses.length > 0) {
        courses.forEach(course => {
          const courseEnrollment = course.enrolledAt?.toDate ? course.enrolledAt.toDate() : new Date(course.enrolledAt);
          if (!earliestEnrollment || courseEnrollment < earliestEnrollment) {
            earliestEnrollment = courseEnrollment;
          }
        });
      }
      
      // Default to user creation date if no courses enrolled
      if (!earliestEnrollment) {
        earliestEnrollment = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt || Date.now());
      }
      
      setEnrollmentDate(earliestEnrollment);
      
      // Check if V12 is due
      const dueInfo = await v12DiagnosticService.checkIfDue(user.uid, earliestEnrollment);
      setV12DueInfo(dueInfo);
      
      // Get current status
      const { status } = await v12DiagnosticService.getStatus(user.uid);
      setV12Status(status);
      
      // Auto-show popup if it's their first time or it's due - but only once per session
      const v12AlreadyShown = getSessionFlag('v12DiagnosticShown');
      if (dueInfo.isDue && dueInfo.isFirstTime && !v12AlreadyShown) {
        // Show popup after a short delay on first time
        setTimeout(() => {
          setShowV12Popup(true);
          setSessionFlag('v12DiagnosticShown', true);
        }, 2000);
      }
    } catch (error) {
      console.error('[V12] Error checking status:', error);
    }
  };

  const handleV12Submit = (result) => {
    console.log('[V12] Diagnostic submitted:', result);
    setShowV12Popup(false);
    // Refresh the status
    checkV12Status();
  };

  const handleOpenV12Diagnostic = () => {
    setShowV12Popup(true);
  };

  const loadUserProgress = async () => {
    setLoading(true);
    const { courses: progressData, error } = await progressService.getAllUserProgress(user.uid);
    
    if (!error && progressData.length > 0) {
      // Fetch course details for each enrolled course
      const coursesWithDetails = [];
      for (const progress of progressData) {
        const { courses } = await courseService.getAllCourses();
        const courseDetail = courses.find(c => c.id === progress.courseId);
        if (courseDetail) {
          coursesWithDetails.push({
            ...courseDetail,
            progress: progress,
          });
        }
      }
      setEnrolledCourses(coursesWithDetails);
    }
    
    setLoading(false);
  };

  const calculateOverallProgress = () => {
    if (enrolledCourses.length === 0) return 0;
    const total = enrolledCourses.reduce((sum, course) => sum + (course.progress?.overallProgress || 0), 0);
    return Math.round(total / enrolledCourses.length);
  };

  const getTotalCompletedModules = () => {
    return enrolledCourses.reduce((sum, course) => sum + (course.progress?.completedModules || 0), 0);
  };

  const getTotalModules = () => {
    return enrolledCourses.reduce((sum, course) => sum + (course.progress?.totalModules || 0), 0);
  };

  const getNextTask = () => {
    if (enrolledCourses.length === 0) return null;

    let nextTask = null;
    let earliestDueDate = null;

    enrolledCourses.forEach(course => {
      const enrollmentDate = course.progress?.enrolledAt?.toDate ? course.progress.enrolledAt.toDate() : new Date();
      const daysSinceEnrollment = Math.floor((new Date() - enrollmentDate) / (1000 * 60 * 60 * 24));

      if (course.chapters && Array.isArray(course.chapters)) {
        course.chapters.forEach((chapter, chapterIndex) => {
          // Get unlock and deadline rules
          const unlockRule = chapter.unlockRule || { type: chapterIndex === 0 ? 'immediate' : 'days', days: chapterIndex * 7 };
          const deadlineRule = chapter.deadlineRule || { type: 'none' };

          // Calculate unlock day
          const unlockDay = unlockRule.type === 'immediate' ? 0 : unlockRule.days || 0;
          const isUnlocked = daysSinceEnrollment >= unlockDay;

          // Calculate deadline
          let dueDate = null;
          if (deadlineRule.type === 'days') {
            const dueDays = unlockDay + (deadlineRule.days || 0);
            dueDate = new Date(enrollmentDate);
            dueDate.setDate(dueDate.getDate() + dueDays);
          }

          // Check if chapter is incomplete
          const chapterComplete = course.progress?.completedChapters?.includes(chapter.id || `chapter-${chapterIndex}`);

          if (!chapterComplete && isUnlocked) {
            // This chapter is available and not complete
            if (dueDate) {
              const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
              
              // Prioritize by deadline
              if (!earliestDueDate || dueDate < earliestDueDate) {
                earliestDueDate = dueDate;
                nextTask = {
                  courseTitle: course.title,
                  courseId: course.id,
                  chapterTitle: chapter.title,
                  chapterIndex,
                  dueDate,
                  daysUntilDue,
                  isOverdue: daysUntilDue < 0,
                };
              }
            } else if (!nextTask) {
              // No deadline, just show first available incomplete chapter
              nextTask = {
                courseTitle: course.title,
                courseId: course.id,
                chapterTitle: chapter.title,
                chapterIndex,
                dueDate: null,
                daysUntilDue: null,
                isOverdue: false,
              };
            }
          } else if (!chapterComplete && !isUnlocked) {
            // Upcoming locked chapter
            const daysUntilUnlock = unlockDay - daysSinceEnrollment;
            if (!nextTask || (nextTask.daysUntilUnlock && daysUntilUnlock < nextTask.daysUntilUnlock)) {
              nextTask = {
                courseTitle: course.title,
                courseId: course.id,
                chapterTitle: chapter.title,
                chapterIndex,
                daysUntilUnlock,
                isLocked: true,
              };
            }
          }
        });
      }
    });

    return nextTask;
  };

  const renderCard = (title, metric, metricChange = null) => {
    return (
      <View style={[styles.cardGlass, { backgroundColor: palette.surface }]}>
        <Text style={[styles.cardTitle, { color: palette.secondaryText }]}>{title}</Text>
        <Text style={[styles.cardMetric, { color: palette.secondaryText }]}>{metric} {metricChange ? <Text style={{ color: palette.primary, fontSize: 13 }}>{metricChange}</Text> : null}</Text>
      </View>
    )
  }

  return (
    <View style={[styles.container]}>
      {/* Geometric Decorative Shapes */}
      <View style={[styles.geometricShape, styles.trianglePink]} />
      <View style={[styles.geometricShape, styles.circleYellow]} />
      <View style={[styles.geometricShape, styles.lineBlack]} />
      <View style={[styles.geometricShape, styles.triangleLavender]} />
      
      <View style={styles.layoutRow}>
        {/* Sidebar */}
        <DashboardSidebar 
          currentPage="dashboard" 
          onOpenV12Diagnostic={handleOpenV12Diagnostic}
          onOpenDailyDecision={openDailyDecision}
          dailyTasks={{
            dailyDecision: dailyDecisionCompleted,
            v12Diagnostic: !v12DueInfo?.isDue
          }}
          weeklyActions={availableWidgets.map(widget => ({
            id: widget.id,
            title: widget.title,
            type: widget.type,
            schedule: widget.schedule,
            completed: widgetStatuses[widget.id]?.completed || false,
            onPress: () => {
              if (widgetStatuses[widget.id]?.completed) {
                openPersistentWidgetReview(widget.id);
              } else {
                openPersistentWidget(widget.id);
              }
            },
          }))}
        />

        {/* Main */}
        <ScrollView style={styles.mainCol} contentContainerStyle={styles.mainContent}>
          {/* Dashboard Header Card */}
          <View style={styles.dashboardHeaderCard}>
            <View style={styles.headerCardContent}>
            <View style={styles.headerLeft}>
                <Text style={styles.dashboardTitle}>
                  {user?.firstName || user?.displayName?.split(' ')[0] || 'Your'}'s Learning Dashboard
                </Text>
              <View style={styles.badgeRow}>
                  {/* Daily Decision Button */}
                  <TouchableOpacity 
                    style={[
                      styles.headerActionButton,
                      dailyDecisionCompleted && styles.headerActionButtonCompleted
                    ]}
                    onPress={dailyDecisionCompleted ? openDailyDecision : openDailyDecision}
                  >
                    <MaterialCommunityIcons 
                      name={dailyDecisionCompleted ? "check-circle" : "head-question-outline"} 
                      size={16} 
                      color={dailyDecisionCompleted ? "#166534" : "#000"} 
                    />
                    <Text style={[
                      styles.headerActionButtonText,
                      dailyDecisionCompleted && styles.headerActionButtonTextCompleted
                    ]}>
                      {dailyDecisionCompleted ? "View Daily Decision" : "Daily Decision"}
                    </Text>
                    {!dailyDecisionCompleted && (
                      <View style={styles.newBadge}>
                        <Text style={styles.newBadgeText}>!</Text>
                </View>
                    )}
                  </TouchableOpacity>
                  
                  {/* V12 Diagnostic Button */}
                  <TouchableOpacity 
                    style={[
                      styles.headerActionButton,
                      styles.headerActionButtonV12,
                      !v12DueInfo?.isDue && styles.headerActionButtonCompleted
                    ]}
                    onPress={handleOpenV12Diagnostic}
                  >
                    <View style={styles.v12BadgeSmall}>
                      <Text style={styles.v12BadgeSmallText}>V12</Text>
                    </View>
                    <Text style={[
                      styles.headerActionButtonText,
                      !v12DueInfo?.isDue && styles.headerActionButtonTextCompleted
                    ]}>
                      Diagnostic
                    </Text>
                    {v12DueInfo?.isDue && (
                      <View style={styles.newBadge}>
                        <Text style={styles.newBadgeText}>!</Text>
                      </View>
                    )}
                  </TouchableOpacity>
              </View>
            </View>
            
            {/* Test Daily Decision Button (admin only) */}
            {user?.userType === 'admin' && (
              <TouchableOpacity 
                  style={styles.testButton}
                onPress={() => {
                  console.log('Force showing daily decision...');
                  forceShowDailyDecision();
                }}
              >
                  <MaterialCommunityIcons name="test-tube" size={16} color="#fff" />
              </TouchableOpacity>
            )}
            </View>
          </View>

          {/* Daily Decision and V12 are now integrated into the header buttons above */}

          {/* Persistent Widget Indicator */}
          <PersistentWidgetIndicator style={styles.persistentWidgetCard} />

          {/* Progress Overview Cards */}
          {loading ? (
            <ActivityIndicator size="large" color={palette.primary || '#FF0600'} style={{ marginTop: 40 }} />
          ) : (
            <>
              <View style={styles.cardsRow}>
                {renderCard('Overall Progress', `${calculateOverallProgress()}%`)}
                {renderCard('Modules Completed', `${getTotalCompletedModules()} / ${getTotalModules()}`)}
                {renderCard('Active Courses', enrolledCourses.length)}
              </View>

              {/* Next Task Section */}
              {(() => {
                const nextTask = getNextTask();
                if (nextTask) {
                  return (
                    <AnimatedCard
                      style={[
                        styles.nextTaskCard,
                        nextTask.isOverdue && styles.nextTaskCardOverdue,
                        nextTask.isLocked && styles.nextTaskCardLocked,
                      ]}
                    >
                      <View style={styles.nextTaskHeader}>
                        <View style={styles.nextTaskIconContainer}>
                          <MaterialCommunityIcons
                            name={nextTask.isLocked ? 'lock-clock' : nextTask.isOverdue ? 'alert' : 'rocket-launch'}
                            size={24}
                            color={nextTask.isOverdue ? '#DC2626' : nextTask.isLocked ? (palette.mutedText || 'rgba(255,255,255,0.5)') : (palette.primary || '#FF0600')}
                          />
                        </View>
                        <Text style={[styles.nextTaskLabel, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
                          {nextTask.isLocked ? '🔒 Coming Soon' : nextTask.isOverdue ? '⚠️ Overdue' : '🎯 Next Task'}
                        </Text>
                      </View>
                      
                      <Text style={[styles.nextTaskTitle, { color: palette.text || 'white' }]}>
                        {nextTask.chapterTitle}
                      </Text>
                      
                      <Text style={[styles.nextTaskCourse, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                        {nextTask.courseTitle}
                      </Text>

                      {nextTask.daysUntilDue !== null && !nextTask.isLocked && (
                        <View style={[styles.nextTaskDeadline, nextTask.isOverdue && styles.nextTaskDeadlineOverdue]}>
                          <MaterialCommunityIcons
                            name="clock-outline"
                            size={16}
                            color={nextTask.isOverdue ? '#DC2626' : (palette.text || 'white')}
                          />
                          <Text style={[styles.nextTaskDeadlineText, { color: nextTask.isOverdue ? '#DC2626' : (palette.text || 'white') }]}>
                            {nextTask.isOverdue
                              ? `Overdue by ${Math.abs(nextTask.daysUntilDue)} ${Math.abs(nextTask.daysUntilDue) === 1 ? 'day' : 'days'}`
                              : `Due in ${nextTask.daysUntilDue} ${nextTask.daysUntilDue === 1 ? 'day' : 'days'}`
                            }
                          </Text>
                        </View>
                      )}

                      {nextTask.isLocked && nextTask.daysUntilUnlock !== undefined && (
                        <View style={styles.nextTaskDeadline}>
                          <MaterialCommunityIcons
                            name="lock-clock"
                            size={16}
                            color={palette.mutedText || 'rgba(255,255,255,0.5)'}
                          />
                          <Text style={[styles.nextTaskDeadlineText, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                            Unlocks in {nextTask.daysUntilUnlock} {nextTask.daysUntilUnlock === 1 ? 'day' : 'days'}
                          </Text>
                        </View>
                      )}

                      {!nextTask.isLocked && (
                        <TouchableOpacity
                          style={[styles.nextTaskButton, { backgroundColor: palette.primary || '#FF0600' }]}
                          onPress={() => navigateTo('Courses')}
                        >
                          <Text style={[styles.nextTaskButtonText, { color: '#fff' }]}>
                            Start Now →
                          </Text>
                        </TouchableOpacity>
                      )}
                    </AnimatedCard>
                  );
                }
                return null;
              })()}

              {/* Course Progress List */}
              <View style={styles.coursesSection}>
                <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Your Courses</Text>
                
                {enrolledCourses.length === 0 ? (
                  <AnimatedCard style={styles.emptyCard}>
                    <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
                      You haven't enrolled in any courses yet.
                    </Text>
                    <TouchableOpacity
                      style={[styles.browseCourses, { borderColor: palette.primary }]}
                      onPress={() => navigateTo('Courses')}
                    >
                      <Text style={[styles.browseCoursesText, { color: palette.primary }]}>Browse Courses</Text>
                    </TouchableOpacity>
                  </AnimatedCard>
                ) : (
                  enrolledCourses.map((course, index) => (
                    <AnimatedCard
                      key={course.id}
                      delay={index * 100}
                      style={styles.courseProgressCard}
                    >
                      <View style={styles.courseProgressHeader}>
                        <View style={styles.courseProgressInfo}>
                          <Text style={[styles.courseProgressTitle, { color: palette.text || 'white' }]}>
                            {course.title}
                          </Text>
                          <Text style={[styles.courseProgressMeta, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
                            {course.progress.completedModules} of {course.progress.totalModules} modules completed
                          </Text>
                        </View>
                        <Text style={[styles.courseProgressPercent, { color: palette.primary }]}>
                          {course.progress.overallProgress}%
                        </Text>
                      </View>

                      {/* Progress Bar */}
                      <View style={styles.progressBarContainer}>
                        <View 
                          style={[
                            styles.progressBarFill, 
                            { 
                              backgroundColor: palette.primary,
                              width: `${course.progress.overallProgress}%`,
                            }
                          ]} 
                        />
                      </View>

                      {/* Last Accessed */}
                      <Text style={[styles.lastAccessed, { color: palette.mutedText || 'rgba(255,255,255,0.5)' }]}>
                        Last accessed: {course.progress.lastAccessedAt?.toDate ? 
                          course.progress.lastAccessedAt.toDate().toLocaleDateString() : 
                          'Never'
                        }
                      </Text>

                      {/* Continue Button */}
                      <TouchableOpacity
                        style={[styles.continueButton, { borderColor: palette.primary }]}
                        onPress={() => navigateTo('Courses')}
                      >
                        <Text style={[styles.continueButtonText, { color: palette.primary }]}>
                          Continue Learning →
                        </Text>
                      </TouchableOpacity>
                    </AnimatedCard>
                  ))
                )}
              </View>
            </>
          )}
        </ScrollView>

        {/* Right Sidebar with Glossary */}
        {showGlossary && (
          <RightSidebar
            showGlossary={showGlossary}
            widgets={[]}
            defaultCollapsed={false}
          />
        )}
      </View>
      
      {/* V12 Diagnostic Popup */}
      <V12DiagnosticPopup
        visible={showV12Popup}
        onClose={() => setShowV12Popup(false)}
        onSubmit={handleV12Submit}
        userId={user?.uid}
        weekNumber={v12DueInfo?.weekNumber}
        monthNumber={v12DueInfo?.monthNumber}
        isFirstTime={v12DueInfo?.isFirstTime}
        previousData={v12Status?.latestCylinderData}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 48,
    paddingRight: 48,
    paddingTop: 160,
    paddingBottom: 40,
    minHeight: '100vh',
    position: 'relative',
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  geometricShape: {
    position: 'absolute',
    zIndex: 0,
  },
  trianglePink: {
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: 80,
    borderRightWidth: 80,
    borderBottomWidth: 138,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#FFB6C1',
    top: 200,
    right: 120,
    transform: [{ rotate: '25deg' }],
  },
  triangleLavender: {
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: 60,
    borderRightWidth: 60,
    borderBottomWidth: 104,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#C5B4E3',
    bottom: 200,
    left: 100,
    transform: [{ rotate: '-15deg' }],
  },
  circleYellow: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFD700',
    top: '40%',
    left: '10%',
  },
  lineBlack: {
    width: 100,
    height: 6,
    backgroundColor: '#000000',
    bottom: '25%',
    right: '15%',
    transform: [{ rotate: '45deg' }],
  },
  layoutRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 24,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainCol: {
    flex: 1,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  glossaryCol: {
    width: 320,
    ...Platform.select({
      web: {
        position: 'sticky',
        top: 100,
      },
    }),
  },
  mainContent: {
    paddingBottom: 60,
    paddingRight: 12,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  dashboardHeaderCard: {
    backgroundColor: '#FF4747',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    ...Platform.select({
      web: {
        boxShadow: '4px 6px 0px rgba(0,0,0,0.9)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 4, height: 6 },
        shadowOpacity: 0.9,
        shadowRadius: 0,
        elevation: 8,
      },
    }),
  },
  headerCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: 1,
    gap: 12,
  },
  dashboardTitle: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,0.5)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.5)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 0,
      },
    }),
  },
  testButton: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  testButtonText: {
    fontSize: 14,
    fontFamily: 'Public Sans',
    fontWeight: '600',
  },
  pageTitle: {
    fontSize: 42,
    fontFamily: 'Chivo',
    fontWeight: '900',
    lineHeight: 48,
    color: '#FFFFFF',
    textShadowColor: 'rgba(0, 0, 0, 0.4)',
    textShadowOffset: { width: 3, height: 3 },
    textShadowRadius: 0,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  headerActionButton: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#4ECDC4',
    borderWidth: 2,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0, 0, 0, 0.85)',
        cursor: 'pointer',
      },
    }),
  },
  headerActionButtonV12: {
    backgroundColor: '#FFE066',
  },
  headerActionButtonCompleted: {
    backgroundColor: '#BBF7D0',
    borderColor: '#22C55E',
  },
  headerActionButtonText: {
    color: '#000000',
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '700',
  },
  headerActionButtonTextCompleted: {
    color: '#166534',
  },
  v12BadgeSmall: {
    backgroundColor: '#D4AF37',
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
  },
  v12BadgeSmallText: {
    fontSize: 8,
    fontFamily: 'Archivo',
    fontWeight: '900',
    color: '#1A1A1A',
    letterSpacing: 0.5,
  },
  newBadge: {
    backgroundColor: '#FF4747',
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 2,
  },
  newBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '900',
  },
  headerRight: {
    position: 'absolute',
    right: 0,
    top: 24,
    flexDirection: 'row',
    gap: 12,
  },
  primaryPill: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryPillText: {
    color: 'white',
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '500',
    lineHeight: 20,
  },
  iconBtnOutline: {
    padding: 8,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardsRow: {
    marginTop: 24,
    flexDirection: 'row',
    gap: 24,
  },
  cardGlass: {
    flex: 1,
    padding: 20,
    borderRadius: 12,
    backgroundColor: '#FF6B6B',
    borderWidth: 4,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '6px 6px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 12,
    textTransform: 'uppercase',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 0,
  },
  cardMetric: {
    color: '#FFFFFF',
    fontSize: 32,
    fontFamily: 'Chivo',
    fontWeight: '900',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 0,
  },
  coursesSection: {
    marginTop: 32,
    gap: 16,
  },
  sectionTitle: {
    fontSize: 32,
    fontFamily: 'Chivo',
    fontWeight: '900',
    marginBottom: 16,
    color: '#FFFFFF',
    textShadowColor: 'rgba(0, 0, 0, 0.4)',
    textShadowOffset: { width: 3, height: 3 },
    textShadowRadius: 0,
  },
  emptyCard: {
    borderWidth: 3,
    borderRadius: 16,
    padding: 40,
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#FFFFFF',
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '5px 5px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  emptyText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  browseCourses: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 22,
    borderWidth: 2,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  browseCoursesText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  courseProgressCard: {
    borderWidth: 4,
    borderRadius: 12,
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
    padding: 24,
    marginBottom: 20,
    gap: 16,
    ...Platform.select({
      web: {
        boxShadow: '8px 8px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  courseProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  courseProgressInfo: {
    flex: 1,
    gap: 4,
  },
  courseProgressTitle: {
    fontSize: 22,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000000',
  },
  courseProgressMeta: {
    fontSize: 14,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.7)',
  },
  courseProgressPercent: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#FF6B6B',
  },
  progressBarContainer: {
    height: 12,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#E0E0E0',
    borderWidth: 2,
    borderColor: '#000000',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: '#4ECDC4',
    ...Platform.select({
      web: {
        transition: 'width 0.3s ease',
      },
    }),
  },
  lastAccessed: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
  },
  continueButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: '#4ECDC4',
    alignSelf: 'flex-start',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '4px 4px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  continueButtonText: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000000',
    textTransform: 'uppercase',
  },
  // Next Task Styles
  nextTaskCard: {
    padding: 28,
    borderRadius: 12,
    borderWidth: 4,
    borderColor: '#000000',
    backgroundColor: '#FFE66D',
    marginTop: 32,
    marginBottom: 32,
    ...Platform.select({
      web: {
        boxShadow: '10px 10px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  nextTaskCardOverdue: {
    backgroundColor: '#FFCDD2',
    borderColor: '#DC2626',
  },
  nextTaskCardLocked: {
    backgroundColor: '#E0E0E0',
    borderColor: '#9E9E9E',
  },
  nextTaskHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  nextTaskIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#000000',
  },
  nextTaskLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  nextTaskTitle: {
    fontSize: 28,
    fontFamily: 'Chivo',
    fontWeight: '900',
    marginBottom: 8,
    lineHeight: 34,
    color: '#000000',
    textShadowColor: 'rgba(255, 255, 255, 0.5)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 0,
  },
  nextTaskCourse: {
    fontSize: 16,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '600',
    marginBottom: 16,
    color: 'rgba(0, 0, 0, 0.7)',
  },
  nextTaskDeadline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 16,
    alignSelf: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0, 0, 0, 0.85)',
      },
    }),
  },
  nextTaskDeadlineOverdue: {
    backgroundColor: '#FFCDD2',
    borderColor: '#DC2626',
  },
  nextTaskDeadlineText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  nextTaskButton: {
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    borderWidth: 4,
    borderColor: '#000000',
    backgroundColor: '#FF6B6B',
    alignItems: 'center',
    marginTop: 8,
    ...Platform.select({
      web: {
        boxShadow: '5px 5px 0px rgba(0, 0, 0, 0.85)',
        cursor: 'pointer',
      },
    }),
  },
  nextTaskButtonText: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  dailyDecisionIndicator: {
    marginHorizontal: 16,
    marginBottom: 16,
  },
  v12Card: {
    marginBottom: 24,
  },
  persistentWidgetCard: {
    marginBottom: 24,
    marginRight: 6,  // Accommodate box shadow
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  reviewSection: {
    marginTop: 20,
    marginRight: 16,
    gap: 12,
    alignSelf: 'flex-end',
    maxWidth: 250,
  },
  reviewButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
    minWidth: 200,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  reviewButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
});

export default MyDashboardScreen;


